import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Button,
    Avatar,
    Stack,
    Chip,
    Divider,
    alpha,
    useTheme,
    CircularProgress,
    IconButton,
    Tabs,
    Tab,
    TextField,
    Alert,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
} from '@mui/material';
import {
    AutoAwesome as MagicIcon,
    Forum as ForumIcon,
    Settings as SettingsIcon,
    CheckCircle as CheckCircleIcon,
    Shield as ShieldIcon,
    AccountBox as LeadIcon,
    SupportAgent as SupportIcon,
    Save as SaveIcon,
    Refresh as RefreshIcon,
    HourglassEmpty as HourglassIcon,
    Check as CheckIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Storefront as StorefrontIcon,
    InfoOutlined as InfoIcon,
    BusinessCenter as BrandIcon,
    StarBorder as InfluencerIcon,
    ExpandMore as ExpandMoreIcon,
    Replay as ReplayIcon,
} from '@mui/icons-material';

import PageAlert from './PageAlert';
import { analyticsApi, automationApi, billingApi } from '../services/api';
import ConnectPlatformsCTA from './ConnectPlatformsCTA';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';
import { FEATURES } from '../constants/features';
import { io } from 'socket.io-client';
import { auth } from '../firebase';

// Small inline info icon with tooltip
function InfoTip({ title }) {
    return (
        <Tooltip title={title} arrow placement="top">
            <InfoIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle', ml: 0.5 }} />
        </Tooltip>
    );
}

// Per-comment outcome row colour map. 'replied'/'queued' are normal happy paths;
// everything else is a "why didn't this comment generate a reply?" diagnostic.
const STATUS_COLOR = {
    replied:         'success',
    queued:          'info',
    replayed:        'info',
    already_replied: 'default',
    skipped:         'warning',
    failed:          'error',
};

const FINAL_STATUSES = new Set(['replied']);

function RefreshLogPanel({ log, reprocessingId, onReprocess, onLoadMore }) {
    if (!log || log.loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }
    if (log.error) {
        return <Alert severity="error">{log.error}</Alert>;
    }
    const entries = log.entries || [];
    if (entries.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                No per-comment activity recorded for this batch.
            </Typography>
        );
    }
    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Comment</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((e) => {
                        const isFinal = FINAL_STATUSES.has(e.status);
                        const isLoading = reprocessingId === e.comment_id;
                        return (
                            <TableRow key={`${e.comment_id}_${e.processed_at || ''}`} hover>
                                <TableCell sx={{ maxWidth: 320 }}>
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        title={e.comment_text
                                            ? (e.comment_text.endsWith('…')
                                                ? e.comment_text + ' [truncated — full text on platform]'
                                                : e.comment_text)
                                            : ''}
                                    >
                                        {e.comment_text || <em style={{ opacity: 0.6 }}>(no text)</em>}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {e.username || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={e.status}
                                        color={STATUS_COLOR[e.status] || 'default'}
                                        sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {e.skip_reason || e.error_message || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={isFinal
                                        ? 'Already replied — send force=true to override'
                                        : 'Re-run AI pipeline for this comment'}>
                                        <span>
                                            <Button
                                                size="small"
                                                variant="text"
                                                disabled={isFinal || isLoading}
                                                startIcon={isLoading
                                                    ? <CircularProgress size={12} color="inherit" />
                                                    : <ReplayIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => onReprocess(e)}
                                                sx={{ textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Re-process
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {log.has_more && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onLoadMore}
                        disabled={log.loadingMore}
                        startIcon={log.loadingMore ? <CircularProgress size={12} color="inherit" /> : null}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                        {log.loadingMore ? 'Loading…' : `Load more (${entries.length} shown)`}
                    </Button>
                </Box>
            )}
        </TableContainer>
    );
}

const EngagementHub = () => {
    const theme = useTheme();
    const { isPlatformEnabled } = useFeatureToggles();
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const hasAutoFetched = useRef(false);

    const [settings, setSettings] = useState({
        enabled: true,
        account_type: 'brand',          // 'brand' | 'influencer'
        persona: 'professional',
        emoji_usage: 'occasionally',
        brand_vocabulary: '',
        response_language: 'English',
        auto_hide_offensive: true,
        lead_qualification: true,
        require_manual_approval: false,
        max_queue_time: 24,
        timeout_action: 'auto_send',
        // ── Brand-specific ──
        brand_context: '',
        high_value_keywords: '',
        cta_lead: '',
        cta_support: '',
        cta_general: '',
        // ── Influencer-specific ──
        creator_bio: '',
        content_niche: '',
        collab_keywords: '',
        cta_collab: '',
        cta_fan: '',
        cta_question: '',
        // Platform toggles
        instagram_enabled: true,
        youtube_enabled: false,
    });

    const [interactions, setInteractions] = useState([]);
    const [pendingReplies, setPendingReplies] = useState([]);
    const [editingReply, setEditingReply] = useState(null); // { id: 'docId', text: 'new text' }
    const [platformFilter, setPlatformFilter] = useState('all'); // all | instagram | facebook | youtube
    const [refreshingComments, setRefreshingComments] = useState(false);
    const [refreshResult, setRefreshResult] = useState(null); // { message, severity }
    const [lastRefresh, setLastRefresh] = useState(null); // { refresh_id, platform, totals, ts } — survives banner timeout
    const [refreshHistory, setRefreshHistory] = useState([]); // [{ refresh_id, platform, totals, ts }, ...]
    const [refreshLog, setRefreshLog] = useState(null); // { entries, loading, error }
    const [reprocessingId, setReprocessingId] = useState(null); // comment_id currently being reprocessed
    const [engagementUsage, setEngagementUsage] = useState(null); // { engagement_daily_count, engagement_daily_limit, engagement_topup_remaining }

    // Unified error extraction
    const errorMsg = typeof error === 'string' ? error : (error?.message || '');
    const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

    useEffect(() => {
        if (!hasAutoFetched.current) {
            hasAutoFetched.current = true;
            fetchSettings();
            fetchInteractions();
            fetchPendingReplies();
            billingApi.getUsage().then(setEngagementUsage).catch(() => {});
        }

        // Connect to Socket.IO backend
        const socket = io(process.env.REACT_APP_API_URL || 'http://127.0.0.1:5001');

        socket.on('connect', async () => {
            console.log('EngagementHub connected to real-time stream');
            // Join the user's room securely using Firebase token
            if (auth.currentUser) {
                try {
                    const token = await auth.currentUser.getIdToken();
                    socket.emit('authenticate', { token });
                } catch (err) {
                    console.error('Failed to get token for socket authentication', err);
                }
            }
        });

        socket.on('engagement_alert', (data) => {
            console.log('New Engagement Alert:', data);
            // Refresh data based on what changed
            if (data.type === 'pending_approval') {
                fetchPendingReplies();
            } else {
                fetchInteractions();
            }
        });

        socket.on('lead_alert', (data) => {
            console.log('Lead Follow-up Alert:', data);
            fetchInteractions();
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const fetchSettings = async () => {
        try {
            const data = await automationApi.getSettings();
            setSettings(data);
        } catch (err) {
            console.error('Failed to fetch settings', err);
            setError(err);
        }
    };

    const fetchInteractions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await analyticsApi.getAll();

            if (data.error && data.error.includes("Firestore features will be disabled")) {
                setError("Firebase Service Account not configured. Automation features are running in limited mode.");
                setInteractions([]);
                return;
            }

            if (!Array.isArray(data)) {
                setInteractions([]);
                return;
            }

            setInteractions(data.filter(i => i.type === 'engagement'));
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingReplies = async () => {
        try {
            const data = await automationApi.getPendingReplies();
            setPendingReplies(data || []);
        } catch (err) {
            console.error('[EngagementHub] fetchPendingReplies failed', err);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await automationApi.updateSettings(settings);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setError('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshComments = async (platform) => {
        if (refreshingComments) return;
        setRefreshingComments(true);
        setRefreshResult(null);
        try {
            const r = await automationApi.refreshComments(platform);
            const { fetched = 0, replied = 0, queued = 0, already_replied = 0,
                    skipped = 0, failed = 0,
                    posts_scanned = 0, posts_total = 0, timed_out = false,
                    refresh_id = null } = r;
            const parts = [];
            if (fetched > 0)         parts.push(`${fetched} new comment${fetched !== 1 ? 's' : ''}`);
            if (replied > 0)         parts.push(`${replied} repl${replied !== 1 ? 'ies' : 'y'} sent`);
            if (queued > 0)          parts.push(`${queued} queued for review`);
            if (already_replied > 0) parts.push(`${already_replied} already replied`);
            if (skipped > 0)         parts.push(`${skipped} skipped`);
            if (failed > 0)          parts.push(`${failed} failed`);
            const postInfo = `${posts_scanned}/${posts_total} posts scanned`;
            const timeoutNote = timed_out ? ' (more posts picked up by next poll)' : '';
            setRefreshResult({
                message: parts.length > 0
                    ? `${parts.join(' · ')} — ${postInfo}${timeoutNote}`
                    : `No new comments — ${postInfo}${timeoutNote}`,
                severity: parts.length > 0 ? 'success' : 'info',
            });
            // Stash refresh_id + totals so the operator can drill into per-comment outcomes.
            // Survives the 8s banner timeout — only cleared on next refresh.
            if (refresh_id) {
                const entry = {
                    refresh_id, platform,
                    totals: { fetched, replied, queued, already_replied, skipped, failed,
                              posts_scanned, posts_total },
                    ts: Date.now(),
                };
                setLastRefresh(entry);
                setRefreshHistory(prev => [entry, ...prev].slice(0, 20)); // keep last 20
                setRefreshLog(null); // force re-fetch when accordion is reopened
            }
            setTimeout(() => setRefreshResult(null), 8000);
            fetchPendingReplies();
        } catch (err) {
            setRefreshResult({ message: err.message || 'Refresh failed', severity: 'error' });
            setTimeout(() => setRefreshResult(null), 5000);
        } finally {
            setRefreshingComments(false);
        }
    };

    const handleLoadRefreshLog = async (refreshId = lastRefresh?.refresh_id) => {
        if (!refreshId) return;
        if (refreshLog?.entries && refreshLog?.refresh_id === refreshId) return; // already loaded this batch
        setRefreshLog({ loading: true, refresh_id: refreshId });
        try {
            const data = await automationApi.getRefreshLog(refreshId);
            setRefreshLog({
                entries: data.entries || [],
                has_more: data.has_more,
                next_cursor: data.next_cursor,
                loading: false,
                refresh_id: refreshId,
            });
        } catch (err) {
            setRefreshLog({ loading: false, error: err.message || 'Failed to load log', refresh_id: refreshId });
        }
    };

    const handleLoadMoreLog = async () => {
        if (refreshLog?.loadingMore) return;
        if (!refreshLog?.has_more || !refreshLog?.next_cursor || !lastRefresh?.refresh_id) return;
        setRefreshLog(prev => ({ ...prev, loadingMore: true }));
        try {
            const data = await automationApi.getRefreshLog(
                lastRefresh.refresh_id, { cursor: refreshLog.next_cursor }
            );
            setRefreshLog(prev => ({
                ...prev,
                entries: [...(prev.entries || []), ...(data.entries || [])],
                has_more: data.has_more,
                next_cursor: data.next_cursor,
                loadingMore: false,
            }));
        } catch (err) {
            setRefreshLog(prev => ({ ...prev, loadingMore: false }));
        }
    };

    const handleReprocess = async (entry) => {
        if (!entry?.comment_id || !entry?.post_id || !entry?.platform) return;
        if (reprocessingId === entry.comment_id) return;
        setReprocessingId(entry.comment_id);
        try {
            const r = await automationApi.reprocessComment({
                platform: entry.platform,
                post_id: entry.post_id,
                comment_id: entry.comment_id,
            });
            setRefreshResult({
                message: r.queued
                    ? 'Comment re-queued — refresh in a few seconds to see the AI reply'
                    : (r.message || 'Comment re-queued'),
                severity: 'success',
            });
            setTimeout(() => setRefreshResult(null), 6000);
            // Optimistic row update so the operator sees something happened
            setRefreshLog((prev) => {
                if (!prev?.entries) return prev;
                const entries = prev.entries.map((e) =>
                    e.comment_id === entry.comment_id
                        ? { ...e, status: 'replayed', skip_reason: 'manual_reprocess' }
                        : e,
                );
                return { ...prev, entries };
            });
            fetchPendingReplies();
        } catch (err) {
            const is409 = err?.response?.status === 409;
            setRefreshResult({
                message: is409
                    ? 'This comment was already replied to. Pass force=true to override.'
                    : (err.message || 'Re-process failed'),
                severity: is409 ? 'warning' : 'error',
            });
            setTimeout(() => setRefreshResult(null), 7000);
        } finally {
            setReprocessingId(null);
        }
    };

    const handleApprove = async (docId) => {
        const textToSubmit = editingReply?.id === docId ? editingReply.text : null;
        try {
            await automationApi.approveReply(docId, textToSubmit);
            setEditingReply(null);
            fetchPendingReplies();
            fetchInteractions(); // Update activity feed
        } catch (err) {
            setError('Failed to approve reply');
        }
    };

    const handleReject = async (docId) => {
        try {
            await automationApi.rejectReply(docId);
            fetchPendingReplies();
        } catch (err) {
            setError('Failed to reject reply');
        }
    };

    if (isNoAccountsError) {
        return (
            <Box sx={{ py: 6 }}>
                <ConnectPlatformsCTA
                    title="Engagement Hub Locked"
                    description="Connect your social media platforms to unlock AI-powered automated replies, lead capture, and community management."
                    buttonText="Link Your Social Empire"
                />
            </Box>
        );
    }

    const renderSettings = () => (
        <Stack spacing={3}>
            {/* ── AI Auto-Responder master card ── */}
            <Card sx={{
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
                <CardContent sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <MagicIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>AI Auto-Responder</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Intelligent 24/7 engagement across your connected platforms
                                </Typography>
                            </Box>
                        </Box>
                        <Tooltip title="Master switch — turns the entire AI reply engine on or off. When off, no automated replies are sent on any platform." arrow>
                            <Switch
                                checked={settings.enabled}
                                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                                color="primary"
                            />
                        </Tooltip>
                    </Box>

                    {/* Platform toggles */}
                    {settings.enabled && (
                        <Box sx={{ mb: 3, pl: 2, borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1.5, display: 'block' }}>
                                Active Platforms
                            </Typography>
                            <Stack spacing={1.5}>
                                {isPlatformEnabled(FEATURES.ENGAGEMENT, 'instagram') && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Instagram Comments
                                                <InfoTip title="The AI monitors your Instagram Business account and automatically replies to new comments every 15 minutes. Only works with Business or Creator accounts." />
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Checks for new comments every 15 min
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={settings.instagram_enabled !== false}
                                            onChange={(e) => setSettings({ ...settings, instagram_enabled: e.target.checked })}
                                            color="secondary"
                                        />
                                    </Box>
                                )}
                                {isPlatformEnabled(FEATURES.ENGAGEMENT, 'youtube') && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                YouTube Comments
                                                <InfoTip title="The AI polls your YouTube channel every 30 minutes and automatically replies to new comments on your uploaded videos." />
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Checks for new comments every 30 min
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={settings.youtube_enabled || false}
                                            onChange={(e) => setSettings({ ...settings, youtube_enabled: e.target.checked })}
                                            color="error"
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={4}>
                        {/* Brand Voice column */}
                        <Grid xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ForumIcon fontSize="small" color="primary" /> Brand Voice
                            </Typography>
                            <Stack spacing={2.5}>
                                <FormControl fullWidth>
                                    <InputLabel id="talk-style-label">Reply Style</InputLabel>
                                    <Select
                                        labelId="talk-style-label"
                                        value={settings.persona || 'professional'}
                                        label="Reply Style"
                                        onChange={(e) => setSettings({ ...settings, persona: e.target.value })}
                                        sx={{ borderRadius: '6px' }}
                                    >
                                        <MenuItem value="professional">Professional & Direct</MenuItem>
                                        <MenuItem value="casual">Casual & Friendly</MenuItem>
                                        <MenuItem value="enthusiastic">High Energy / Hype</MenuItem>
                                        <MenuItem value="witty">Witty & Humorous</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        Tone the AI uses in every reply
                                        <InfoTip title="'Professional & Direct' suits B2B or formal brands. 'Casual & Friendly' works well for lifestyle or consumer brands. 'High Energy' is great for fitness or entertainment. 'Witty' adds personality." />
                                    </FormHelperText>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id="emoji-label">Emoji Usage</InputLabel>
                                    <Select
                                        labelId="emoji-label"
                                        value={settings.emoji_usage || 'occasionally'}
                                        label="Emoji Usage"
                                        onChange={(e) => setSettings({ ...settings, emoji_usage: e.target.value })}
                                        sx={{ borderRadius: '6px' }}
                                    >
                                        <MenuItem value="never">Never</MenuItem>
                                        <MenuItem value="occasionally">Occasionally</MenuItem>
                                        <MenuItem value="often">Often</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        How often emojis appear in replies
                                        <InfoTip title="'Never' keeps replies formal and text-only. 'Occasionally' adds emojis where they feel natural. 'Often' gives replies a fun, expressive feel — great for younger audiences." />
                                    </FormHelperText>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id="lang-label">Reply Language</InputLabel>
                                    <Select
                                        labelId="lang-label"
                                        value={settings.response_language || 'English'}
                                        label="Reply Language"
                                        onChange={(e) => setSettings({ ...settings, response_language: e.target.value })}
                                        sx={{ borderRadius: '6px' }}
                                    >
                                        <MenuItem value="English">English</MenuItem>
                                        <MenuItem value="Spanish">Spanish</MenuItem>
                                        <MenuItem value="French">French</MenuItem>
                                        <MenuItem value="Portuguese">Portuguese</MenuItem>
                                        <MenuItem value="Arabic">Arabic</MenuItem>
                                        <MenuItem value="Hindi">Hindi</MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        Language used for all AI replies
                                        <InfoTip title="The AI will compose all replies in this language, regardless of the language of the incoming comment. Match this to your primary audience's language." />
                                    </FormHelperText>
                                </FormControl>

                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Brand Phrases"
                                    placeholder="cheers, fam, drop us a DM"
                                    value={settings.brand_vocabulary || ''}
                                    onChange={(e) => setSettings({ ...settings, brand_vocabulary: e.target.value })}
                                    helperText={<>Comma-separated words the AI weaves in naturally <InfoTip title="These phrases will be organically included in replies when contextually appropriate — not forced. Good for maintaining a consistent brand voice (e.g. 'cheers', 'legend', 'let's go')." /></>}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                />
                            </Stack>
                        </Grid>

                        {/* Safety & Moderation column */}
                        <Grid xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShieldIcon fontSize="small" color="primary" /> Safety & Moderation
                            </Typography>
                            <Stack spacing={2}>
                                <Box sx={{ p: 2, borderRadius: '6px', border: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <FormControlLabel
                                                control={<Switch checked={settings.auto_hide_offensive} onChange={(e) => setSettings({ ...settings, auto_hide_offensive: e.target.checked })} />}
                                                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Hide Offensive Comments <InfoTip title="The AI detects hate speech, profanity, and harmful content using sentiment analysis. Matching comments are automatically hidden from your public audience — you can still view them in your platform dashboard." /></Typography>}
                                            />
                                        </Box>
                                        <Box>
                                            <FormControlLabel
                                                control={<Switch checked={settings.lead_qualification} onChange={(e) => setSettings({ ...settings, lead_qualification: e.target.checked })} />}
                                                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Automatic Lead Capture <InfoTip title="When a comment contains high-value buying-intent keywords (set below), it's flagged as a lead and you receive an email alert with the commenter's details and message." /></Typography>}
                                            />
                                        </Box>
                                    </Stack>
                                </Box>

                                <Box>
                                    <FormControlLabel
                                        control={<Switch checked={settings.require_manual_approval} onChange={(e) => setSettings({ ...settings, require_manual_approval: e.target.checked })} color="warning" />}
                                        label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Require Manual Approval <InfoTip title="Instead of posting instantly, every AI-generated reply goes into a review queue (Moderation Queue tab). You can approve, edit, or reject each reply before it goes live." /></Typography>}
                                    />
                                    {settings.require_manual_approval && (
                                        <Box sx={{ mt: 2, p: 2.5, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: '6px', border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary', fontWeight: 500 }}>
                                                If you are offline, queued replies will auto-expire to prevent stale responses. Configure how that works:
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid xs={12} sm={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Queue Timeout</InputLabel>
                                                        <Select
                                                            value={settings.max_queue_time}
                                                            label="Queue Timeout"
                                                            onChange={(e) => setSettings({ ...settings, max_queue_time: e.target.value })}
                                                            sx={{ borderRadius: '6px' }}
                                                        >
                                                            <MenuItem value={4}>4 Hours</MenuItem>
                                                            <MenuItem value={12}>12 Hours</MenuItem>
                                                            <MenuItem value={24}>24 Hours</MenuItem>
                                                            <MenuItem value={48}>48 Hours</MenuItem>
                                                        </Select>
                                                        <FormHelperText>
                                                            How long a reply waits before expiring
                                                            <InfoTip title="After this duration passes without approval, the reply is handled automatically based on the 'On Timeout' setting. Shorter windows keep your responses timely." />
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                                <Grid xs={12} sm={6}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>On Timeout</InputLabel>
                                                        <Select
                                                            value={settings.timeout_action}
                                                            label="On Timeout"
                                                            onChange={(e) => setSettings({ ...settings, timeout_action: e.target.value })}
                                                            sx={{ borderRadius: '6px' }}
                                                        >
                                                            <MenuItem value="auto_send">Auto-Send AI Reply</MenuItem>
                                                            <MenuItem value="auto_drop">Delete (Do Not Send)</MenuItem>
                                                        </Select>
                                                        <FormHelperText>
                                                            Action when the queue expires
                                                            <InfoTip title="'Auto-Send' posts the AI's reply automatically after the timeout — good for high-volume accounts. 'Delete' discards it silently — safer if you'd rather not reply late." />
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>

                        {/* Account Type + Context */}
                        <Grid xs={12}>
                            <Divider sx={{ my: 2 }} />

                            {/* Account Type Toggle */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Account Type</Typography>
                                <Stack direction="row" spacing={2}>
                                    {[
                                        {
                                            value: 'brand',
                                            icon: <BrandIcon sx={{ fontSize: 28 }} />,
                                            label: 'Brand',
                                            desc: 'Sales funnels, lead gen & customer support',
                                            activeColor: '#22C55E',
                                            activeBg: alpha('#22C55E', 0.10),
                                            activeBorder: '#22C55E',
                                        },
                                        {
                                            value: 'influencer',
                                            icon: <InfluencerIcon sx={{ fontSize: 28 }} />,
                                            label: 'Influencer / Creator',
                                            desc: 'Community building, collabs & audience engagement',
                                            activeColor: '#9c27b0',
                                            activeBg: alpha('#9c27b0', 0.10),
                                            activeBorder: '#9c27b0',
                                        },
                                    ].map(({ value, icon, label, desc, activeColor, activeBg, activeBorder }) => {
                                        const active = settings.account_type === value;
                                        return (
                                            <Box
                                                key={value}
                                                onClick={() => setSettings({ ...settings, account_type: value })}
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    p: 2,
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    border: `2px solid ${active ? activeBorder : alpha(theme.palette.divider, 0.3)}`,
                                                    bgcolor: active ? activeBg : 'transparent',
                                                    transition: 'all 0.18s ease',
                                                    '&:hover': {
                                                        border: `2px solid ${activeBorder}`,
                                                        bgcolor: activeBg,
                                                    },
                                                }}
                                            >
                                                <Box sx={{
                                                    color: active ? activeColor : 'text.disabled',
                                                    transition: 'color 0.18s ease',
                                                    display: 'flex',
                                                }}>
                                                    {icon}
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" sx={{
                                                        fontWeight: 800,
                                                        color: active ? activeColor : 'text.primary',
                                                        lineHeight: 1.2,
                                                        transition: 'color 0.18s ease',
                                                    }}>
                                                        {label}
                                                        {active && (
                                                            <Chip
                                                                label="Active"
                                                                size="small"
                                                                sx={{
                                                                    ml: 1,
                                                                    height: 18,
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 700,
                                                                    bgcolor: activeColor,
                                                                    color: '#fff',
                                                                    '& .MuiChip-label': { px: 1 },
                                                                }}
                                                            />
                                                        )}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3 }}>
                                                        {desc}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>

                            {settings.account_type !== 'influencer' ? (
                                /* ── Brand Settings ── */
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <StorefrontIcon fontSize="small" color="primary" /> Business Context & CTAs
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Brand Context & Offers"
                                                placeholder="We sell organic dog food. New customers get 20% off with code NEW20."
                                                value={settings.brand_context || ''}
                                                onChange={(e) => setSettings({ ...settings, brand_context: e.target.value })}
                                                helperText={<>Describe your business so AI replies stay on-brand <InfoTip title="This context is injected into every AI reply prompt. Include your product/service, target audience, current promotions, and anything that helps the AI sound like your brand." /></>}
                                                sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="High-Value Keywords"
                                                placeholder="price, buy, order, where, how much"
                                                value={Array.isArray(settings.high_value_keywords) ? settings.high_value_keywords.join(', ') : settings.high_value_keywords || ''}
                                                onChange={(e) => setSettings({ ...settings, high_value_keywords: e.target.value.split(',').map(s => s.trim()) })}
                                                helperText={<>Triggers lead alerts & priority flagging <InfoTip title="When any comment contains one of these words, it's instantly classified as a high-value lead. You'll get a real-time email alert and the comment gets flagged in the activity feed." /></>}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                            />
                                        </Grid>
                                        <Grid xs={12} md={6}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Lead CTA"
                                                    placeholder="DM us 'READY' for a quote!"
                                                    value={settings.cta_lead || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_lead: e.target.value })}
                                                    helperText={<>Appended to replies on purchase / pricing comments <InfoTip title="When the AI detects a buying-intent comment, it appends this CTA to guide the commenter to your sales funnel." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Support CTA"
                                                    placeholder="Please email support@example.com."
                                                    value={settings.cta_support || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_support: e.target.value })}
                                                    helperText={<>Appended to replies on complaints / issues <InfoTip title="Used when the AI classifies a comment as a support request or complaint. Directs the customer to the right channel." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="General CTA"
                                                    placeholder="Thanks for following! Link in bio."
                                                    value={settings.cta_general || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_general: e.target.value })}
                                                    helperText={<>Appended to replies on praise / casual comments <InfoTip title="Used for general positive comments. Keeps replies engaging and guides followers to take an action." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                /* ── Influencer / Creator Settings ── */
                                <>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfluencerIcon fontSize="small" color="secondary" /> Creator Profile & Engagement CTAs
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="About Me / Creator Bio"
                                                placeholder="Fitness & wellness creator | Certified PT | Helping you build sustainable habits 💪 Based in NYC."
                                                value={settings.creator_bio || ''}
                                                onChange={(e) => setSettings({ ...settings, creator_bio: e.target.value })}
                                                helperText={<>AI uses this to personalise replies and sound like you <InfoTip title="Describe yourself, your niche, and your personality. The AI will use this to craft replies that feel authentic to your voice." /></>}
                                                sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                            />
                                            <TextField
                                                fullWidth
                                                label="Content Niche"
                                                placeholder="fitness, nutrition, lifestyle, mental health"
                                                value={settings.content_niche || ''}
                                                onChange={(e) => setSettings({ ...settings, content_niche: e.target.value })}
                                                helperText={<>Comma-separated topics that define your content <InfoTip title="These topics keep the AI focused on your content area so it answers niche-specific audience questions accurately." /></>}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                            />
                                        </Grid>
                                        <Grid xs={12} md={6}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Collab / Sponsor Keywords"
                                                    placeholder="collab, sponsor, PR, gifting, partnership, paid"
                                                    value={settings.collab_keywords || ''}
                                                    onChange={(e) => setSettings({ ...settings, collab_keywords: e.target.value })}
                                                    helperText={<>Triggers priority flag for brand-deal enquiries <InfoTip title="Comments containing these words are flagged as potential collab opportunities and you'll receive a real-time alert." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Collab Enquiry CTA"
                                                    placeholder="DM me your media kit — always open to collabs! 🙌"
                                                    value={settings.cta_collab || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_collab: e.target.value })}
                                                    helperText={<>Appended to replies on collab / sponsor comments <InfoTip title="When the AI detects a brand deal enquiry, this CTA guides them to your preferred contact." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Fan Appreciation CTA"
                                                    placeholder="Thanks for the love! Check my link in bio 💕"
                                                    value={settings.cta_fan || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_fan: e.target.value })}
                                                    helperText={<>Appended to replies on fan / praise comments <InfoTip title="Used for positive fan interactions — keeps your audience engaged and drives them to your link in bio." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label="Question Reply CTA"
                                                    placeholder="Great Q! Save this post & follow for more tips ✨"
                                                    value={settings.cta_question || ''}
                                                    onChange={(e) => setSettings({ ...settings, cta_question: e.target.value })}
                                                    helperText={<>Appended when the AI answers audience questions <InfoTip title="Encourages followers to save the post or follow for more content after the AI answers their question." /></>}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>

                    {saved && (
                        <Alert severity="success" sx={{ mt: 3, borderRadius: '6px' }}>
                            Settings updated successfully
                        </Alert>
                    )}

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveSettings}
                            disabled={saving}
                            sx={{ borderRadius: '4px', px: 4, fontWeight: 700 }}
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

        </Stack>
    );

    const renderInteractions = () => (
        <Stack spacing={2}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : interactions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: alpha(theme.palette.background.paper, 0.4), borderRadius: '16px' }}>
                    <ForumIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        No high-value interactions detected yet. AI is monitoring your profile...
                    </Typography>
                </Box>
            ) : (
                interactions.map((item, idx) => (
                    <Card key={idx} sx={{
                        borderRadius: '16px',
                        border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                        '&:hover': { bgcolor: alpha(theme.palette.background.paper, 0.8) }
                    }}>
                        <CardContent sx={{ display: 'flex', gap: 2 }}>
                            <Avatar sx={{ bgcolor: item.intent === 'lead' ? 'success.main' : 'info.main' }}>
                                {item.intent === 'lead' ? <LeadIcon /> : <SupportIcon />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {item.intent === 'lead' ? 'New Potential Lead' : 'Customer Support Inquiry'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {item.platform && (
                                            <Chip
                                                label={item.platform}
                                                size="small"
                                                color={item.platform === 'instagram' ? 'secondary' : item.platform === 'youtube' ? 'error' : 'default'}
                                                sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                            />
                                        )}
                                        <Chip
                                            label={item.intent.toUpperCase()}
                                            size="small"
                                            color={item.intent === 'lead' ? 'success' : 'info'}
                                            sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                                        />
                                    </Box>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1.5, p: 1.5, bgcolor: alpha(theme.palette.text.primary, 0.03), borderRadius: '6px' }}>
                                    "{item.comment_text}"
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                                    <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        AI Replied: {String(item.ai_response || '')}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );

    const renderModerationQueue = () => {
        const counts = pendingReplies.reduce((acc, r) => {
            const p = (r.platform || 'unknown').toLowerCase();
            acc[p] = (acc[p] || 0) + 1;
            return acc;
        }, {});
        const filtered = platformFilter === 'all'
            ? pendingReplies
            : pendingReplies.filter(r => (r.platform || '').toLowerCase() === platformFilter);

        const platformTabs = [
            { value: 'all',       label: 'All',       count: pendingReplies.length },
            { value: 'instagram', label: 'Instagram', count: counts.instagram || 0 },
            { value: 'facebook',  label: 'Facebook',  count: counts.facebook  || 0 },
            { value: 'youtube',   label: 'YouTube',   count: counts.youtube   || 0 },
        ];

        return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Tabs
                    value={platformFilter}
                    onChange={(_, v) => setPlatformFilter(v)}
                    sx={{
                        borderBottom: 1, borderColor: 'divider', minHeight: 40,
                        '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 40 },
                    }}
                >
                    {platformTabs.map(t => (
                        <Tab
                            key={t.value}
                            value={t.value}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    {t.label}
                                    <Chip label={t.count} size="small" sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700 }} />
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                {platformFilter !== 'all' && (
                    <Tooltip title={`Fetch new comments on your ${platformFilter} posts and generate AI replies for unreplied ones`}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={refreshingComments
                                ? <CircularProgress size={14} color="inherit" />
                                : <RefreshIcon />}
                            onClick={() => handleRefreshComments(platformFilter)}
                            disabled={refreshingComments}
                            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}
                        >
                            {refreshingComments ? 'Refreshing…' : `Refresh ${platformFilter.charAt(0).toUpperCase() + platformFilter.slice(1)} Comments`}
                        </Button>
                    </Tooltip>
                )}
            </Box>

            {refreshResult && (
                <Alert severity={refreshResult.severity} sx={{ borderRadius: '8px' }}>
                    {refreshResult.message}
                </Alert>
            )}

            <Accordion
                disabled={!lastRefresh?.refresh_id}
                onChange={(_, expanded) => { if (expanded) handleLoadRefreshLog(); }}
                sx={{
                    borderRadius: '12px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {lastRefresh?.refresh_id
                                ? 'Refresh log'
                                : 'Refresh log — click Refresh Comments above to populate'}
                        </Typography>
                        {/* History picker — stops click from bubbling to accordion toggle */}
                        {refreshHistory.length > 1 && (
                            <FormControl
                                size="small"
                                variant="standard"
                                onClick={e => e.stopPropagation()}
                                sx={{ minWidth: 180 }}
                            >
                                <Select
                                    disableUnderline
                                    value={lastRefresh?.refresh_id || ''}
                                    onChange={e => {
                                        const chosen = refreshHistory.find(h => h.refresh_id === e.target.value);
                                        if (chosen) {
                                            setLastRefresh(chosen);
                                            setRefreshLog(null);
                                            handleLoadRefreshLog(chosen.refresh_id);
                                        }
                                    }}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    {refreshHistory.map(h => (
                                        <MenuItem key={h.refresh_id} value={h.refresh_id}>
                                            {h.platform} — {new Date(h.ts).toLocaleTimeString()}
                                            {h.totals?.failed > 0 && ` ⚠ ${h.totals.failed} failed`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {lastRefresh?.platform && refreshHistory.length <= 1 && (
                            <Chip size="small" label={lastRefresh.platform}
                                sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }} />
                        )}
                        {lastRefresh?.totals?.skipped > 0 && (
                            <Chip size="small" color="warning"
                                label={`${lastRefresh.totals.skipped} skipped`}
                                sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {lastRefresh?.totals?.failed > 0 && (
                            <Chip size="small" color="error"
                                label={`${lastRefresh.totals.failed} failed`}
                                sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <RefreshLogPanel
                        log={refreshLog}
                        reprocessingId={reprocessingId}
                        onReprocess={handleReprocess}
                        onLoadMore={handleLoadMoreLog}
                    />
                </AccordionDetails>
            </Accordion>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: alpha(theme.palette.background.paper, 0.4), borderRadius: '16px' }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2, opacity: 0.8 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {platformFilter === 'all' ? 'All Caught Up!' : `No ${platformFilter} replies pending`}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        There are no pending AI replies waiting for your approval.
                    </Typography>
                </Box>
            ) : (
                filtered.map((item, idx) => (
                    <Card key={idx} sx={{
                        borderRadius: '16px',
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        bgcolor: alpha(theme.palette.warning.main, 0.02),
                        position: 'relative',
                        overflow: 'visible'
                    }}>
                        <Box sx={{
                            position: 'absolute', top: -12, right: 24, bgcolor: 'warning.main', color: 'warning.contrastText',
                            px: 2, py: 0.5, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 800,
                            display: 'flex', alignItems: 'center', gap: 0.5
                        }}>
                            <HourglassIcon sx={{ fontSize: 14 }} /> Pending Approval
                        </Box>

                        {item.is_high_value && (
                            <Box sx={{
                                position: 'absolute', top: -12, left: 24, bgcolor: 'warning.main', color: 'warning.contrastText',
                                px: 2, py: 0.5, borderRadius: '6px', fontSize: '0.75rem', fontWeight: 800,
                                display: 'flex', alignItems: 'center', gap: 0.5
                            }}>
                                <MagicIcon sx={{ fontSize: 14 }} /> HIGH VALUE
                            </Box>
                        )}

                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Avatar sx={{ bgcolor: item.intent === 'lead' ? 'success.main' : 'info.main' }}>
                                    {item.intent === 'lead' ? <LeadIcon /> : <SupportIcon />}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {item.intent === 'lead' ? 'Potential Lead' : 'Customer Comment'}
                                        </Typography>
                                        <Chip
                                            label={item.platform || 'unknown'}
                                            size="small"
                                            color={item.platform === 'instagram' ? 'secondary' : item.platform === 'youtube' ? 'error' : item.platform === 'facebook' ? 'primary' : 'default'}
                                            sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                        />
                                        {item.username && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                @{item.username}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        "{item.comment_text}"
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            Received {
                                                (() => {
                                                    const d = item.createdAt?.toDate ? item.createdAt.toDate()
                                                        : item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000)
                                                            : new Date(item.createdAt);
                                                    return isNaN(d?.getTime()) ? 'Unknown' : d.toLocaleString();
                                                })()
                                            }
                                        </Typography>
                                        {item.media_id && (
                                            <Chip
                                                label={`Post: ${String(item.media_id).slice(0, 18)}${String(item.media_id).length > 18 ? '…' : ''}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 18, fontSize: '0.65rem', fontFamily: 'monospace' }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.8), p: 2, borderRadius: '6px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <MagicIcon sx={{ fontSize: 14 }} /> AI Proposed Reply
                                </Typography>

                                {editingReply?.id === item.id ? (
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={editingReply.text}
                                        onChange={(e) => setEditingReply({ ...editingReply, text: e.target.value })}
                                        variant="outlined"
                                        size="small"
                                        sx={{ mb: 2, bgcolor: theme.palette.background.paper }}
                                    />
                                ) : (
                                    <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: '4px' }}>
                                        {String(item.ai_response || '')}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleReject(item.id)}
                                        startIcon={<DeleteIcon />}
                                    >
                                        Reject
                                    </Button>

                                    {editingReply?.id === item.id ? (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() => setEditingReply(null)}
                                        >
                                            Cancel Options
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            color="inherit"
                                            onClick={() => setEditingReply({ id: item.id, text: item.ai_response })}
                                            startIcon={<EditIcon />}
                                        >
                                            Edit
                                        </Button>
                                    )}

                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleApprove(item.id)}
                                        startIcon={<CheckIcon />}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {editingReply?.id === item.id ? 'Save & Send' : 'Approve & Send'}
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 1 }}>
                        Engagement Hub
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Monitor and automate your business conversations
                    </Typography>
                </Box>
                <IconButton onClick={fetchInteractions} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            <PageAlert error={error} />

            {/* ── Daily engagement usage bar ── */}
            {engagementUsage && (
                <Card sx={{
                    mb: 4,
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: 'blur(20px)',
                    borderRadius: '16px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                            {/* Daily replies progress */}
                            <Box sx={{ flex: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                        AI Replies Today
                                    </Typography>
                                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                        <Typography sx={{
                                            fontWeight: 800, fontSize: '1rem',
                                            color: engagementUsage.engagement_daily_count >= engagementUsage.engagement_daily_limit
                                                ? theme.palette.error.main
                                                : engagementUsage.engagement_daily_count / engagementUsage.engagement_daily_limit > 0.8
                                                    ? theme.palette.warning.main
                                                    : theme.palette.success.main,
                                        }}>
                                            {engagementUsage.engagement_daily_count.toLocaleString()}
                                        </Typography>
                                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                            / {engagementUsage.engagement_daily_limit.toLocaleString()}
                                        </Typography>
                                    </Stack>
                                </Stack>
                                {/* Progress bar */}
                                <Box sx={{
                                    position: 'relative', height: 8, borderRadius: '100px',
                                    bgcolor: alpha(theme.palette.divider, 0.2),
                                    overflow: 'hidden', mb: 0.75,
                                }}>
                                    {(() => {
                                        const pct = engagementUsage.engagement_daily_limit > 0
                                            ? Math.min(100, (engagementUsage.engagement_daily_count / engagementUsage.engagement_daily_limit) * 100)
                                            : 0;
                                        const barColor = pct >= 100
                                            ? 'linear-gradient(90deg, #ef4444, #f97316)'
                                            : pct > 80
                                                ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
                                                : `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`;
                                        return (
                                            <Box sx={{
                                                position: 'absolute', left: 0, top: 0, bottom: 0,
                                                width: `${pct}%`,
                                                borderRadius: '100px',
                                                background: barColor,
                                                transition: 'width 0.8s ease',
                                            }} />
                                        );
                                    })()}
                                </Box>
                                <Typography color="text.secondary" sx={{ fontSize: '0.78rem' }}>
                                    {engagementUsage.engagement_daily_count >= engagementUsage.engagement_daily_limit
                                        ? 'Daily plan quota reached — top-up credits are being used'
                                        : `${(engagementUsage.engagement_daily_limit - engagementUsage.engagement_daily_count).toLocaleString()} replies remaining today`}
                                </Typography>
                            </Box>

                            {/* Topup balance */}
                            <Box sx={{
                                minWidth: 140, px: 2.5, py: 2, borderRadius: '12px',
                                bgcolor: alpha(theme.palette.warning.main, engagementUsage.engagement_topup_remaining > 0 ? 0.1 : 0.04),
                                border: `1px solid ${alpha(theme.palette.warning.main, engagementUsage.engagement_topup_remaining > 0 ? 0.3 : 0.12)}`,
                                textAlign: 'center',
                            }}>
                                <Typography sx={{
                                    fontWeight: 900, fontSize: '1.75rem', letterSpacing: '-0.03em', lineHeight: 1,
                                    color: engagementUsage.engagement_topup_remaining > 0
                                        ? theme.palette.warning.main
                                        : theme.palette.text.disabled,
                                }}>
                                    {engagementUsage.engagement_topup_remaining.toLocaleString()}
                                </Typography>
                                <Typography color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5, fontWeight: 600 }}>
                                    top-up credits
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 4, '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', fontSize: '1rem' } }}
            >
                <Tab icon={<SettingsIcon sx={{ mr: 1 }} />} iconPosition="start" label="Automation Settings" />
                <Tab
                    icon={
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <HourglassIcon sx={{ mr: 1 }} />
                            {pendingReplies.length > 0 && (
                                <Box sx={{
                                    position: 'absolute', top: -8, left: -8,
                                    bgcolor: 'error.main', color: 'white',
                                    borderRadius: '50%', width: 16, height: 16,
                                    fontSize: '0.65rem', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}>
                                    {pendingReplies.length}
                                </Box>
                            )}
                        </Box>
                    }
                    iconPosition="start"
                    label="Moderation Queue"
                />
                <Tab icon={<ForumIcon sx={{ mr: 1 }} />} iconPosition="start" label="AI Activity Feed" />
            </Tabs>

            {tab === 0 && renderSettings()}
            {tab === 1 && renderModerationQueue()}
            {tab === 2 && renderInteractions()}
        </Box>
    );
};

export default EngagementHub;
